import { SavedPayment, YCCommercePayments } from '@yc-commerce/payments-frontend-sdk';
import getConfig from 'next/config';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useDecision } from '@optimizely/react-sdk';
import { CreditCardInfo, GiftCardInfo, PaymentType } from '@/domain/payments/types';
import { AddCreditCardInputs } from '../profile/paymentMethods/rails/CreditCardRail/types';
import { openModal } from '@/localization/actions';

const transformYumSavedPaymentsToLegacyCards = (yumCreditCards: SavedPayment[]): CreditCardInfo[] => yumCreditCards.map((yumCreditCard) => ({
  paymentId: yumCreditCard.payment_method_id,
  isDefault: yumCreditCard?.default ?? false,
  paymentType: 'creditcard',
  name: yumCreditCard.name ?? '',
  cardInfo: {
    cardType: yumCreditCard.card_type,
    lastFour: yumCreditCard.last_four,
    expiration: `${yumCreditCard?.expiry_month ?? ''}/${yumCreditCard?.expiry_year ?? ''}`,
    postalCode: yumCreditCard?.postal_code ?? ''
  }
}));

export type SavedGiftCard = SavedPayment & { balance: { current_balance: { amount: number } } };

export const transformYumGiftCardToLegacy = (yumGiftCard: SavedGiftCard): GiftCardInfo => ({
  paymentId: yumGiftCard.payment_method_id,
  isDefault: yumGiftCard?.default ?? false,
  paymentType: PaymentType.giftcard,
  cardInfo: {
    number: yumGiftCard.last_four,
    balance: yumGiftCard.balance.current_balance.amount
  }
})

const transformYumSavedPaymentsToLegacyGiftCards = (yumGiftCards: SavedGiftCard[] ): GiftCardInfo[] => yumGiftCards.map(transformYumGiftCardToLegacy);
type GiftCardBalanceCheckOptions = {
  cardNumber: string;
  pin: string;
  onSuccess: (balance: { amount: number; currency_code: string }) => void;
  onError: (error: Error) => void;
}

type UseYumPaymentSDK = [
  {
    railVisible: boolean;
    savedPaymentMethods?: CreditCardInfo[];
    savedGiftCards?: GiftCardInfo[];
    sdkLoading: boolean;
    paymentFormLoading: boolean;
    primaryChecked: boolean;
    formOperation: string;
  },
  {
    yumUpdateSavedPaymentMethod(cardId: string, values: AddCreditCardInputs): void;
    yumPayDeleteSavedPaymentMethod(paymentMethodId: string): void;
    yumPaySetDefaultSavedPaymentMethod(paymentMethodId: string): void;
    setPrimaryChecked(value: React.SetStateAction<boolean>): void;
    setRailVisible(value: React.SetStateAction<boolean>): void;
    setFormOperation(value: React.SetStateAction<string>): void;
    submitYumPayForm(): void;
    yumPayRenderUserSaveCard(): void;
    closeYumPayIFrame(): void;
    saveGiftCard(cardNumber: string, pin: string, onSuccess: () => void, onError: () => void): void;
    checkGiftCardBalance(options: GiftCardBalanceCheckOptions): void;
    getSavedPaymentCards(): void;
  }
];

const useYumPaymentSDK = ({ yumToken }: { yumToken: string }): UseYumPaymentSDK => {
  // Yum iframe loads in a large empty container, so we want to hide that prior to onReady firing off
  const [paymentFormLoading, setPaymentFormLoading] = useState(true);
  const [savedPaymentMethods, setSavedPaymentMethods] = useState<undefined | CreditCardInfo[]>(undefined);
  const [savedGiftCards, setSavedGiftCards] = useState<undefined | GiftCardInfo[]>(undefined);
  const [sdkSessionInitialized, setSDKSessionInitialized] = useState(false);
  // ui state
  // TODO: Move UI state unrelated to Yum Payments SDK to relevant component
  const [primaryChecked, setPrimaryChecked] = useState(false);
  const [railVisible, setRailVisible] = useState(false);
  const [formOperation, setFormOperation] = useState('');

  const { publicRuntimeConfig } = getConfig();
  const dispatch = useDispatch();
  const sdk = React.useRef<YCCommercePayments | null>(null);
  const apiUrlOverride = publicRuntimeConfig?.YUM_ECOM_API_URL_OVERRIDE;
  
  const [{ enabled: savedGiftCardsEnabled }] = useDecision('fr-web-4181-saved-gift-cards');

  // Set SDK
  useEffect(() => {
    if (!sdk.current && yumToken) {
      const env = publicRuntimeConfig.ENVIRONMENT_NAME;
      sdk.current = new YCCommercePayments({
        environment: env === 'prod' ? 'production' : 'staging',
        access_token: yumToken,
        org_id: 'ph_us',
        debug: env !== 'prod',
        api_url_override: apiUrlOverride || undefined
      });
    }
  }, [yumToken, apiUrlOverride, publicRuntimeConfig.ENVIRONMENT_NAME]);

  const resetSessionWithNewData = () => {
    if (sdk?.current) {
      setRailVisible(false);
      setPrimaryChecked(false);
    }
  };

  const handleError = (err: Error) => {
    dispatch(openModal({
      title: 'We\'re sorry',
      body: 'Something went wrong. Please try again.',
      cta: {
        text: 'OK'
      }
    }));
    resetSessionWithNewData();
    console.error(err);
  };

  const getSavedPaymentCards = (resetSDK?: boolean) => {
    if (sdk?.current) {
      sdk.current.getSavedPaymentMethods({
        include_expiration_date: true,
        include_postal_code: true,
        include_prepaid_balance: savedGiftCardsEnabled,
        onSuccess: (recievedPaymentMethods) => {
          const transformedCreditCards = transformYumSavedPaymentsToLegacyCards(recievedPaymentMethods.filter(paymentMethod => paymentMethod.card_type !== 'GIFT_CARD'));
          setSavedPaymentMethods(transformedCreditCards);
          const transformedGiftCards = transformYumSavedPaymentsToLegacyGiftCards((recievedPaymentMethods as SavedGiftCard[]).filter(paymentMethod => paymentMethod.card_type === 'GIFT_CARD'));
          setSavedGiftCards(transformedGiftCards);
          if (!savedGiftCardsEnabled && resetSDK) resetSessionWithNewData();
        },
        onError: handleError
      });
    }
  };

  const startSDKSession = () => {
    if (sdk?.current) {
      setPaymentFormLoading(true);
      sdk.current.startSession({
        app_type: 'web',
        onSuccess: () => {
          setSDKSessionInitialized(true);
        },
        onError: handleError
      });
    }
  };

  // Tie our startSession to the railOpening
  useEffect(() => {
    if (sdk?.current && railVisible) {
      startSDKSession();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [railVisible]);

  // Initialize SDK
  useEffect(() => {
    if (sdk?.current && !sdkSessionInitialized) {
      startSDKSession();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sdkSessionInitialized]);

  const yumPaySetDefaultSavedPaymentMethod = (paymentMethodId: string) => {
    if (sdkSessionInitialized && sdk?.current) {
      sdk.current.setDefaultSavedPaymentMethod({
        payment_method_id: paymentMethodId,
        onSuccess: () => {
          getSavedPaymentCards(!savedGiftCardsEnabled);
        },
        onError: handleError
      });
    } else {
      console.error('SDK has not been initialized.');
    }
  };

  // Operation Handlers
  const yumPayDeleteSavedPaymentMethod = (paymentMethodId: string) => (sdkSessionInitialized && sdk?.current
    ? sdk.current.deleteSavedPaymentMethod({
      payment_method_id: paymentMethodId,
      onSuccess: () => {
        getSavedPaymentCards();
      },
      onError: handleError
    })
    : console.error('SDK has not been initialized.'));

  const yumUpdateSavedPaymentMethod = (cardId: string, values: AddCreditCardInputs) => {
    const {
      expirationMonth, expirationYear, nameOnCard, primary
    } = values;
    if (sdk?.current) {
      sdk.current.updateSavedPaymentMethod({
        payment_method_id: cardId,
        expiry_month: expirationMonth,
        expiry_year: expirationYear,
        name: nameOnCard,
        onSuccess: () => {
          if (primary) {
            yumPaySetDefaultSavedPaymentMethod(cardId);
          } else {
            getSavedPaymentCards(!savedGiftCardsEnabled);
          }
        },
        onError: handleError
      });
    }
  };

  // iFrame Render Methods
  const yumPayRenderUserSaveCard = () => {
    if (sdkSessionInitialized && sdk?.current) {
      sdk.current.updateSession({
        onSuccess: () => {
          if (sdk?.current) {
            sdk.current.captureCard({
              html_element_id: 'yc-payments-cardcapture',
              page_type: 'GUEST_CHECKOUT',
              create_persistent_token: true,
              auto_exit: false,
              onReady: () => {
                setPaymentFormLoading(false);
              },
              onSuccess: (token: Record<string, any>) => {
                if (token.payment_method && sdk?.current) {
                  let paymentToken = token.payment_method;

                  try {
                    const updatedToken = JSON.parse(window.atob(token.payment_method));
                    updatedToken.token.store = true;
                    paymentToken = window.btoa(JSON.stringify(updatedToken));
                  } catch (error) {
                    handleError(error as Error);
                  }

                  sdk.current.createSavedPaymentMethod({
                    payment_token: paymentToken,
                    onSuccess: (savedPaymentData) => {
                      if (primaryChecked) {
                        // Disabled ts due to wrong type in iFrame node module
                        // eslint-disable-next-line
                        // @ts-ignore
                        yumPaySetDefaultSavedPaymentMethod(savedPaymentData.paymentMethodId as string);
                      } else {
                        getSavedPaymentCards(!savedGiftCardsEnabled);
                      }
                      
                      if (savedGiftCardsEnabled) {
                        setRailVisible(false);
                      }
                    },
                    onError: handleError
                  });
                }
              },
              onError: handleError
            });
          }
        },
        onError: handleError
      });
    } else {
      console.error('SDK has not been initialized.');
    }
  };

  const submitYumPayForm = () => {
    if (!paymentFormLoading && sdk?.current) {
      sdk.current.submitHostedPage();
    }
  };

  const saveGiftCard = (cardNumber: string, pin: string, onSuccess: () => void, onError: () => void) => {
    if (sdkSessionInitialized && sdk?.current) {
      const cardNumberWithPin = `${cardNumber.replace(/\s/g, '')}${pin.replace(/\s/g, '')}`;
      const paymentToken = sdk.current.tokenizeGiftCard({ card_number: cardNumberWithPin });
      sdk.current.createSavedPaymentMethod({
        payment_token: paymentToken,
        onSuccess: () => {
          getSavedPaymentCards();
          onSuccess();
        },
        onError: (error) => {
          handleError(error);
          onError();
      }});
    }
  }
  
  const checkGiftCardBalance = ({ cardNumber, pin, onSuccess, onError }: GiftCardBalanceCheckOptions) => {
    if (!sdk?.current) return;

    const cardNumberWithPin = `${cardNumber.replace(/\s/g, '')}${pin.replace(/\s/g, '')}`;

    sdk.current.giftCardBalanceByCardNumber({
      card_number: cardNumberWithPin,
      onSuccess,
      onError
    });
  }

  const closeSdkForms = (formLoaded: boolean) => formLoaded && sdk?.current && sdk?.current?.stop();

  const closeYumPayIFrame = () => {
    closeSdkForms(!paymentFormLoading);
    setRailVisible(false);
    setPrimaryChecked(false);
  };

  return [
    {
      sdkLoading: !sdkSessionInitialized,
      savedPaymentMethods,
      savedGiftCards,
      paymentFormLoading,
      railVisible,
      primaryChecked,
      formOperation
    },
    {
      yumUpdateSavedPaymentMethod,
      yumPayDeleteSavedPaymentMethod,
      yumPaySetDefaultSavedPaymentMethod,
      yumPayRenderUserSaveCard,
      closeYumPayIFrame,
      submitYumPayForm,
      setRailVisible,
      setPrimaryChecked,
      setFormOperation,
      saveGiftCard,
      checkGiftCardBalance,
      getSavedPaymentCards
    }
  ];
};

export default useYumPaymentSDK;
